/**
 * API hook for assessments
 */
import { useCallback } from "react";
import { API_BASE } from "../../config";
import { useAuth } from "../../auth/AuthProvider";
import { reportPaymentRequired } from "../../lib/api-client";
import { mapItem, type SendQuestionnaireParams, type UploadedAssessment } from "./types";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useAssessmentsApi(patientId: string, sessionId?: string) {
  const { ensureToken } = useAuth();

  // HTTP API helper (JWT authorizer)
  const apiJson = useCallback(async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const token = await ensureToken();
    const resp = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, ...(init.headers || {}) },
    });

    const text = await resp.text();
    let data: any = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      console.error("[useAssessmentsApi] JSON parse failed:", { path, status: resp.status, text });
      throw new Error(`HTTP ${resp.status} (non-JSON response)`);
    }

    if (!resp.ok) {
      reportPaymentRequired(resp.status, data); // questionnaire sends are Pro-gated
      throw new Error(data?.error || `HTTP ${resp.status}`);
    }
    return data as T;
  }, [ensureToken]);

  const loadList = useCallback(async (signal?: AbortSignal): Promise<UploadedAssessment[]> => {
    if (!patientId?.trim()) return [];

    const qs = new URLSearchParams({ patient_id: patientId.trim() });
    if (sessionId?.trim()) qs.set("session_id", sessionId.trim());

    const token = await ensureToken();
    const resp = await fetch(`${API_BASE}/assessments?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });

    const text = await resp.text();
    let data: any = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(`HTTP ${resp.status} (non-JSON response)`);
    }

    if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);

    const rawItems: any[] = Array.isArray(data?.items) ? data.items : [];
    return rawItems.map(mapItem).sort((a, b) => b.createdAt - a.createdAt);
  }, [patientId, sessionId, ensureToken]);

  const fetchOne = useCallback(async (assessmentId: string): Promise<UploadedAssessment> => {
    const qs = new URLSearchParams({ patient_id: patientId.trim(), assessment_id: assessmentId });
    const data = await apiJson<{ item?: any; items?: any[] }>(`/assessments/item?${qs.toString()}`);
    const it = data.item ?? data.items?.[0] ?? null;
    if (!it) throw new Error("Malformed response: missing item");
    return mapItem(it);
  }, [patientId, apiJson]);

  const deleteOne = useCallback(async (assessmentId: string) => {
    const qs = new URLSearchParams({ patient_id: patientId.trim(), assessment_id: assessmentId });
    return apiJson(`/assessments/item?${qs.toString()}`, { method: "DELETE" });
  }, [patientId, apiJson]);

  // Task 5c: mark an email-channel "sent" questionnaire completed by hand
  // (patch_questionnaire.py — PATCH /patients/{pid}/questionnaires/{id}).
  // Pro-gated (402) like sendQuestionnaire; apiJson already reports that.
  // Returns the backend's partial patch payload (NOT a full list/get row —
  // callers must merge these fields into existing state, not replace it).
  const markCompleted = useCallback(async (assessmentId: string): Promise<{
    assessmentId: string;
    status: "completed";
    completedManually: true;
    completedAt: number;
  }> => {
    const data = await apiJson<{ item?: any }>(
      `/patients/${encodeURIComponent(patientId.trim())}/questionnaires/${encodeURIComponent(assessmentId)}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "mark_completed" }),
      },
    );
    if (!data.item) throw new Error("Malformed response: missing item");
    return {
      assessmentId: String(data.item.assessment_id ?? assessmentId),
      status: "completed",
      completedManually: true,
      completedAt: Number(data.item.completed_at),
    };
  }, [patientId, apiJson]);

  // Plan 2 Task 4: channel is now REQUIRED by send_questionnaire.py, and the
  // send targets either a built-in instrument_id or a custom template
  // file_id (never both) — see spec §3 / SendQuestionnaireDialog's gating.
  const sendQuestionnaire = useCallback(async (params: SendQuestionnaireParams) => {
    const body: Record<string, unknown> = { channel: params.channel, lang: params.lang };
    if (params.instrumentId) body.instrument_id = params.instrumentId;
    if (params.fileId) body.file_id = params.fileId;
    if (params.attachOriginal) body.attach_original = true;
    return apiJson(`/patients/${encodeURIComponent(patientId.trim())}/questionnaires`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }, [patientId, apiJson]);

  const saveParserText = useCallback(async (assessmentId: string, parserText: string) => {
    const qs = new URLSearchParams({ patient_id: patientId.trim(), assessment_id: assessmentId });
    return apiJson(`/assessments/item?${qs.toString()}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ parser_text: parserText }),
    });
  }, [patientId, apiJson]);

  const uploadFile = useCallback(async (file: File): Promise<void> => {
    const token = await ensureToken();
    const params = new URLSearchParams({
      parser_id: "schema_default",
      type: "schema",
      filename: file.name,
      patient_id: patientId.trim(),
    });
    if (sessionId?.trim()) params.set("session_id", sessionId.trim());

    const resp = await fetch(`${API_BASE}/assessments/upload?${params.toString()}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: file,
    });

    const text = await resp.text();
    let data: any = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(`Upload failed: HTTP ${resp.status} (non-JSON response)`);
    }

    if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
  }, [patientId, sessionId, ensureToken]);

  const startParse = useCallback(async (a: UploadedAssessment, parserTextOverride?: string) => {
    const tp_pk = a.tpPk?.trim();
    const sk = a.sk?.trim();

    if (!tp_pk || !sk) {
      throw new Error("Cannot parse: missing tp_pk/sk on this item");
    }

    const token = await ensureToken();
    const qs = new URLSearchParams({ tp_pk, sk });

    // Fire-and-forget via async tick (202). Frontend already polls the
    // assessment item directly via waitForParseDone.
    const resp = await fetch(`${API_BASE}/assessments/parse?${qs.toString()}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(parserTextOverride ? { parser_text: parserTextOverride } : {}),
    });

    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      throw new Error(`Parse ${resp.status}: ${txt || "(empty body)"}`);
    }

    return resp.json();
  }, [ensureToken]);

  const waitForParseDone = useCallback(async (
    assessmentId: string,
    onUpdate: (fresh: UploadedAssessment) => void
  ): Promise<UploadedAssessment> => {
    const maxMs = 120_000;
    const stepMs = 1200;
    const t0 = Date.now();

    while (Date.now() - t0 < maxMs) {
      const fresh = await fetchOne(assessmentId);
      onUpdate(fresh);

      if (fresh.parseStatus === "ok" || fresh.parseStatus === "error") {
        return fresh;
      }

      await sleep(stepMs);
    }
    throw new Error("Parse timed out (still pending).");
  }, [fetchOne]);

  return {
    loadList,
    fetchOne,
    // getAssessment: GET /assessments/item (get_assessment.py) is the SAME
    // endpoint fetchOne already calls — it's the one that returns the full
    // instrument_snapshot + responses once a row is expanded. Exposed under
    // this name too so the answers-expand call site reads clearly.
    getAssessment: fetchOne,
    deleteOne,
    // cancelSend: the guarded DELETE /assessments/item (delete_assessment.py,
    // Task 5b) doubles as "cancel a still-sent questionnaire" — same request
    // as deleteOne, named for the questionnaire call site's intent.
    cancelSend: deleteOne,
    markCompleted,
    sendQuestionnaire,
    saveParserText,
    uploadFile,
    startParse,
    waitForParseDone,
  };
}
